import CoreAPIService from '../../../utils/core-api';
import { fetchAction } from '../../actions';
import { push } from 'connected-react-router';
import ObjectUtils from '../../../utils/objectUtils';
import { types as pricingTypes } from '../pricing';
import { Airplus, CompanyById, GroupById, Industry, PaymentSettings, Sepa } from '../api-types';
import { paths } from '../../../routes/paths';
import envConfig from '../../../config';
import { UpdateCompaniesKamToKamPayload } from '../ws-types';

export type { CompanyById };

export const name: string = 'companies';

export const types = {
    GET_COMPANY: name + '/GET_COMPANY',
    GET_COMPANY_REPORT: name + '/GET_COMPANY_REPORT',
    EXPORT_COMPANY_TRIPS: name + '/EXPORT_COMPANY_TRIPS',
    EXPORT_COMPANY_USERS: name + '/EXPORT_COMPANY_USERS',
    SAVE_COMPANY: name + '/SAVE_COMPANY',
    EDIT_COMPANY: name + '/EDIT_COMPANY',
    DELETE_COMPANY: name + '/DELETE_COMPANY',
    SAVE_COMPANY_DEPARTMENT: name + '/SAVE_COMPANY_DEPARTMENT',
    SAVE_COMPANY_MEMBER: name + '/SAVE_COMPANY_MEMBER',
    EDIT_COMPANY_MEMBER: name + '/EDIT_COMPANY_MEMBER',
    SAVE_COMPANY_CONTRACT: name + '/SAVE_COMPANY_CONTRACT',
    GET_COMPANY_CONTRACT: name + '/GET_COMPANY_CONTRACT',
    EDIT_COMPANY_CONTRACT: name + '/EDIT_COMPANY_CONTRACT',
    DELETE_COMPANY_CONTRACT: name + '/DELETE_COMPANY_CONTRACT',
    BAN_DRIVER_FROM_COMPANY: name + '/BAN_DRIVER_FROM_COMPANY',
    UNBAN_DRIVER_FROM_COMPANY: name + '/UNBAN_DRIVER_FROM_COMPANY',
    EDIT_COMPANY_DISPATCH_GROUPS: name + '/EDIT_COMPANY_DISPATCH_GROUPS',
    GET_FID_PROGRAM: name + '/GET_FID_PROGRAM',
    CREATE_FID_PROGRAM: name + '/CREATE_FID_PROGRAM',
    DELETE_FID_PROGRAM: name + '/DELETE_FID_PROGRAM',
    GET_TRAVEL_POLICIES: name + '/GET_TRAVEL_POLICIES',
    CREATE_TRAVEL_POLICY: name + '/CREATE_TRAVEL_POLICY',
    DELETE_TRAVEL_POLICIES: name + '/DELETE_TRAVEL_POLICIES',
    GET_INDUSTRIES: name + '/GET_INDUSTRIES',
    GET_NEW_INDUSTRIES: name + '/GET_NEW_INDUSTRIES',
    GET_COMPANY_GROUP_POLICIES: name + '/GET_COMPANY_GROUP_POLICIES',
    EDIT_COMPANY_GROUP_POLICIES: name + '/EDIT_COMPANY_GROUP_POLICIES',
    SET_CONTRACT_GROUPS_AUTOVALIDATE: name + '/SET_CONTRACT_GROUPS_AUTOVALIDATE',
    SET_CONTRACT_MULTIGROUP: name + '/SET_CONTRACT_MULTIGROUP',
    FORCE_BILLING: name + '/FORCE_BILLING',

    // NEW Company - Contract
    GET_CONTRACT: name + '/GET_CONTRACT',
    EDIT_CONTRACT: name + '/EDIT_CONTRACT',
    GET_CONTRACT_GROUP: name + '/GET_CONTRACT_GROUP',
    GET_CONTRACT_GROUPS: name + '/GET_CONTRACT_GROUPS',
    GET_CONTRACT_PAYMENT_SETTINGS: name + '/GET_CONTRACT_PAYMENT_SETTINGS',
    SET_GROUP_PAYMENT_SETTINGS: name + '/SET_GROUP_PAYMENT_SETTINGS',
    CREATE_PAYMENT_METHOD_SEPA: name + '/CREATE_PAYMENT_METHOD_SEPA',
    CREATE_PAYMENT_METHOD_AIRPLUS: name + '/CREATE_PAYMENT_METHOD_AIRPLUS',
    // KAM to KAM
    GET_KAM_CONTACT_COUNT: name + '/GET_KAM_CONTACT_COUNT',
    UPDATE_CONTRACT_KAM_TO_KAM: name + '/UPDATE_CONTRACT_KAM_TO_KAM',
};

interface CompaniesState {
    groupsNew: GroupById;
    companiesNew: CompanyById;
    companies: CompanyById;
    companies_list: {};
    contracts: {};
    industries: Industry[];
    industriesNew: Industry[];
}
const initialState: CompaniesState = {
    groupsNew: {} as GroupById,
    companiesNew: {} as CompanyById,
    companies: {} as CompanyById,
    companies_list: {},
    contracts: {},
    industries: [],
    industriesNew: [],
};

const reducer = (state = initialState, action): CompaniesState => {
    const payload = action.payload;
    const parameters = action.parameters;
    const id = parameters ? parameters.id : 0;
    const idc = parameters ? parameters.idc : 0;
    const company = parameters ? parameters.company : 0;

    let newState;
    switch (action.type) {
        case types.GET_CONTRACT:
        case types.EDIT_CONTRACT:
        // LT-881: `PUT /internal/contract/:contract` ends on `getContractDetails`,
        // so its response is the GET's payload and merges here unchanged. Without
        // this case the store kept `multigroup_enabled: false` after a successful
        // activation, so the checkbox stayed unticked — and, since the block reads
        // `readOnly` from the same value, still editable, offering to activate a
        // contract that already was.
        case types.SET_CONTRACT_MULTIGROUP:
            return {
                ...state,
                companiesNew: {
                    ...state.companiesNew,
                    [id]: { ...state.companiesNew[id], ...payload },
                },
            };

        case types.GET_CONTRACT_GROUP:
            return {
                ...state,
                groupsNew: {
                    ...state.groupsNew,
                    [id]: { ...state.groupsNew[id], ...payload },
                },
            };
        case types.GET_CONTRACT_GROUPS:
            return {
                ...state,
                companiesNew: {
                    ...state.companiesNew,
                    [id]: { ...state.companiesNew[id], groups: payload.data },
                },
            };
        case types.GET_CONTRACT_PAYMENT_SETTINGS:
            return {
                ...state,
                companiesNew: {
                    ...state.companiesNew,
                    [id]: { ...state.companiesNew[id], payment_settings: payload },
                },
            };

        case types.GET_COMPANY:
        case types.EDIT_COMPANY:
            action = {
                ...action,
                payload: {
                    ...action.payload,
                    documents: ObjectUtils.keepIds(payload.documents),
                },
            };
            return {
                ...state,
                companies: {
                    ...state.companies,
                    [id]: { ...state.companies[id], ...action.payload },
                },
            };

        case types.GET_COMPANY_CONTRACT:
            return {
                ...state,
                contracts: { ...state.contracts, [id]: payload },
            };
        case types.EDIT_COMPANY_CONTRACT:
            return {
                ...state,
                contracts: {
                    ...state.contracts,
                    [payload.current_version.id]: payload.current_version,
                },
            };
        case types.BAN_DRIVER_FROM_COMPANY:
            return {
                ...state,
                companies: {
                    ...state.companies,
                    [id]: {
                        ...state.companies[id],
                        banDrivers: [...state.companies[id].banDrivers, payload],
                    },
                },
            };
        case types.UNBAN_DRIVER_FROM_COMPANY:
            const idb = parameters ? parameters.blacklistId : 0;
            newState = ObjectUtils.deepCopy(state);
            newState.companies[id].banDrivers = newState.companies[id].banDrivers.filter((x) => x.id !== idb);
            return newState;

        // COMPANY_PRICING_SET
        case pricingTypes.DELETE_COMPANY_PRICING_SET:
            const company_to_pricing_set_id = parameters ? parameters.company_to_pricing_set_id : 0;
            if (Object.keys(state.companies).length === 0 || !idc) {
                return state;
            }
            return {
                ...state,
                companies: {
                    ...state.companies,
                    [idc]: {
                        ...state.companies[idc],
                        pricing_sets: state.companies[idc].pricing_sets.filter(
                            (x) => x.company_to_pricing_set_id !== company_to_pricing_set_id,
                        ),
                    },
                },
            };

        // GROUP_PRICING_SET
        // Deliberately not modelled on CREATE_COMPANY_PRICING_SET above: that
        // one pushes into state before returning it and throws outright when
        // the slice is absent.
        case pricingTypes.CREATE_GROUP_PRICING_SET: {
            const pricingSet = parameters ? parameters.pricing_set : null;
            if (!idc || !state.companies[idc] || !pricingSet || !payload?.data) {
                return state;
            }
            // The API answers with the link row, which has no name and no
            // service; both come from the sheet the user picked.
            const attached = {
                ...pricingSet,
                group_to_pricing_set_id: payload.data.id,
                start_date: payload.data.start_date,
                end_date: payload.data.end_date,
                priority: payload.data.priority,
            };
            return {
                ...state,
                companies: {
                    ...state.companies,
                    [idc]: {
                        ...state.companies[idc],
                        group_pricing_sets: [...(state.companies[idc].group_pricing_sets ?? []), attached],
                        // A successful read proves the call works, so a stale
                        // failure flag from an earlier load must not survive it.
                        group_pricing_sets_failed: false,
                    },
                },
            };
        }

        case pricingTypes.DELETE_GROUP_PRICING_SET: {
            const group_to_pricing_set_id = parameters ? parameters.group_to_pricing_set_id : 0;
            if (!idc || !state.companies[idc]?.group_pricing_sets) {
                return state;
            }
            return {
                ...state,
                companies: {
                    ...state.companies,
                    [idc]: {
                        ...state.companies[idc],
                        group_pricing_sets: state.companies[idc].group_pricing_sets.filter(
                            (x) => x.group_to_pricing_set_id !== group_to_pricing_set_id,
                        ),
                    },
                },
            };
        }

        // COMPANY_EXTRA
        case pricingTypes.DELETE_COMPANY_EXTRA:
            const company_to_extra_id = parameters ? parameters.company_to_extra_id : 0;
            newState = ObjectUtils.deepCopy(state);
            newState.companies[id].management_fee = newState.companies[id].management_fee.filter(
                (x) => x.company_to_extra_id !== company_to_extra_id,
            );
            return newState;

        case pricingTypes.CREATE_COMPANY_EXTRA:
            newState = ObjectUtils.deepCopy(state);
            if (!newState.companies[company.id].management_fee) {
                newState.companies[company.id].management_fee = [];
            }
            newState.companies[company.id].management_fee.push(payload.data);

            return newState;
        // FID
        case types.GET_FID_PROGRAM:
            return {
                ...state,
                companies: {
                    ...state.companies,
                    [id]: {
                        ...state.companies[id],
                        fid_programs: [
                            ...(Array.isArray(payload.data) ? payload.data : [payload.data].filter(Boolean)),
                        ],
                    },
                },
            };
        case types.CREATE_FID_PROGRAM:
            return {
                ...state,
                companies: {
                    ...state.companies,
                    [id]: {
                        ...state.companies[id],
                        fid_programs: [
                            ...(state.companies[id].fid_programs || []),
                            ...(Array.isArray(payload.data) ? payload.data : [payload.data].filter(Boolean)),
                        ],
                    },
                },
            };
        case types.DELETE_FID_PROGRAM:
            return {
                ...state,
                companies: {
                    ...state.companies,
                    [id]: {
                        ...state.companies[id],
                        fid_programs: state.companies[id].fid_programs.map((p) =>
                            p.id === payload.data.id ? { ...p, deleted_at: payload.data.deleted_at } : p,
                        ),
                    },
                },
            };
        // Company Group Policy
        case types.GET_COMPANY_GROUP_POLICIES:
        case types.EDIT_COMPANY_GROUP_POLICIES:
            if (state.companiesNew[id]) {
                return {
                    ...state,
                    companiesNew: {
                        ...state.companiesNew,
                        [id]: { ...state.companiesNew[id], group_policies: payload.group?.[0] },
                    },
                };
            } else {
                return {
                    ...state,
                    groupsNew: {
                        ...state.groupsNew,
                        [id]: { ...state.groupsNew[id], group_policies: payload.group?.[0] },
                    },
                };
            }
        // Travel Policy
        case types.GET_TRAVEL_POLICIES:
        case types.CREATE_TRAVEL_POLICY:
        case types.DELETE_TRAVEL_POLICIES:
            return {
                ...state,
                companies: {
                    ...state.companies,
                    [id]: {
                        ...state.companies[id],
                        travel_policies: payload,
                    },
                },
            };
        // Industries
        case types.GET_INDUSTRIES:
            return {
                ...state,
                industries: payload,
            };
        case types.GET_NEW_INDUSTRIES:
            return {
                ...state,
                industriesNew: payload,
            };
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    getCompany: (id, callback?) =>
        fetchAction(
            CoreAPIService.get(`/core/companies/${id}`),
            types.GET_COMPANY,
            { id },
            {
                success: callback,
            },
        ),
    getCompanyReport: (id) =>
        fetchAction(CoreAPIService.get(`/core/companies/${id}/report`), types.GET_COMPANY_REPORT, { id }),
    exportCompanyTrips: (id, params) =>
        fetchAction(CoreAPIService.get(`/core/companies/${id}/exports/trips`, params), types.EXPORT_COMPANY_TRIPS, {
            id,
        }),
    exportCompanyUsers: (id, params) =>
        fetchAction(CoreAPIService.get(`/core/companies/${id}/exports/users`, params), types.EXPORT_COMPANY_USERS, {
            id,
        }),
    saveCompany: (payload) =>
        fetchAction(
            CoreAPIService.post(`/core/companies`, payload),
            types.SAVE_COMPANY,
            {},
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.companies}/`));
                },
            },
        ),
    editCompany: (id, payload) =>
        fetchAction(
            CoreAPIService.put(`/core/companies/${id}`, payload),
            types.EDIT_COMPANY,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.companies}/${id}`));
                },
            },
        ),
    editCompanyDispatchGroups: (id, payload, callback) =>
        fetchAction(
            CoreAPIService.put(`/core/companies/${id}/dispatch_groups`, payload),
            types.EDIT_COMPANY_DISPATCH_GROUPS,
            {
                id,
            },
            {
                success: callback,
            },
        ),
    deleteCompany: (id, parentId) =>
        fetchAction(
            CoreAPIService.delete(`/core/companies/${id}`),
            types.DELETE_COMPANY,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.companies}/${parentId}`));
                },
            },
        ),

    saveDepartment: (id, payload) =>
        fetchAction(
            CoreAPIService.post(`/core/companies/${id}/departments`, payload),
            types.SAVE_COMPANY_DEPARTMENT,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.companies}/${id}`));
                },
            },
        ),
    saveMember: (id, payload) =>
        fetchAction(
            CoreAPIService.post(`/core/riders`, payload),
            types.SAVE_COMPANY_MEMBER,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.companies}/${id}`));
                },
            },
        ),
    editMember: (id, idm, payload, type, callback) =>
        fetchAction(
            CoreAPIService.put(`/core/companies/${id}/memberships/${idm}`, payload),
            types.EDIT_COMPANY_MEMBER,
            { id, idm, payload, type },
            {
                success: callback,
            },
        ),

    saveContract: (id, payload) =>
        fetchAction(
            CoreAPIService.post(`/core/documents`, payload),
            types.SAVE_COMPANY_CONTRACT,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.companies}/${id}`));
                },
            },
        ),
    getContract: (id) =>
        fetchAction(CoreAPIService.get(`/core/documents/version/${id}`), types.GET_COMPANY_CONTRACT, { id }),

    editContract: (idc, id, payload) =>
        fetchAction(
            CoreAPIService.post(`/core/documents/${idc}`, payload),
            types.EDIT_COMPANY_CONTRACT,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.companies}/${id}`));
                },
            },
        ),
    deleteContract: (id, payload, callback) =>
        fetchAction(
            CoreAPIService.put(`/core/documents/${id}/version`, payload),
            types.DELETE_COMPANY_CONTRACT,
            { id },
            {
                success: callback,
            },
        ),
    banDriverFromCompany: (id, idc, callback) =>
        fetchAction(
            CoreAPIService.put(`/core/companies/${id}/drivers/${idc}`, { ban: true }),
            types.BAN_DRIVER_FROM_COMPANY,
            { id },
            { success: callback },
        ),
    unbanDriverFromCompany: (id, idc, blacklistId) =>
        fetchAction(
            CoreAPIService.put(`/core/companies/${id}/drivers/${idc}`, {
                ban: false,
                blacklist_id: blacklistId,
            }),
            types.UNBAN_DRIVER_FROM_COMPANY,
            { id, blacklistId },
        ),
    getFidProgram: (id, uuid: string, uuidParent?: string) =>
        fetchAction(
            CoreAPIService.get(
                `/rewards/loyalty/programs/company/${uuid}${uuidParent ? `?id_parent=${uuidParent}` : ''}`,
            ),
            types.GET_FID_PROGRAM,
            {
                id,
            },
        ),
    createFidProgram: (id, payload, callback) =>
        fetchAction(
            CoreAPIService.post(`/rewards/loyalty/programs`, payload),
            types.CREATE_FID_PROGRAM,
            {
                id,
            },
            { success: callback },
        ),
    deleteProgram: (id, idCompany) =>
        fetchAction(CoreAPIService.delete(`/rewards/loyalty/programs/${id}`), types.DELETE_FID_PROGRAM, {
            id: idCompany,
        }),
    getCompanyIndustries: () => fetchAction(CoreAPIService.get(`/core/industry`), types.GET_INDUSTRIES, {}),
    getCompanyNewIndustries: () =>
        fetchAction(
            CoreAPIService.get(`internal/contract/industries`, null, null, envConfig.privapi_url),
            types.GET_NEW_INDUSTRIES,
            {},
        ),
    getCompanyGroupPolicies: (idGroup: string, uuidContract: string) =>
        fetchAction(
            CoreAPIService.get(
                `policies?groups[]=${idGroup}&enrich_dispatch_groups=1`,
                null,
                null,
                envConfig.privapi_url,
            ),
            types.GET_COMPANY_GROUP_POLICIES,
            { id: uuidContract },
        ),
    editCompanyGroupPolicies: (uuidContract: string, payload, callback) =>
        fetchAction(
            CoreAPIService.post(`policies`, payload, null, envConfig.privapi_url),
            types.EDIT_COMPANY_GROUP_POLICIES,
            { id: uuidContract },
            { success: callback },
        ),
    setContractGroupsAutovalidate: (uuidContract: string, payload, callback, onError?) =>
        fetchAction(
            CoreAPIService.patch(`policies/contracts/${uuidContract}`, payload, null, envConfig.privapi_url),
            types.SET_CONTRACT_GROUPS_AUTOVALIDATE,
            { id: uuidContract },
            { success: callback, error: onError },
        ),
    // LT-881 — the Multigroupe flag, which lives on the contract itself rather
    // than on its group policies. Deliberately not `editCompanyNew`, whose
    // success callback navigates to the contract page: this one is saved from
    // a settings block the operator is already standing in, and the flag is
    // one-way, so a refusal has to reach `onError` instead of a redirect.
    setContractMultigroup: (uuidContract: string, payload, callback, onError?) =>
        fetchAction(
            CoreAPIService.put(`internal/contract/${uuidContract}`, payload, null, envConfig.privapi_url),
            types.SET_CONTRACT_MULTIGROUP,
            { id: uuidContract },
            { success: callback, error: onError },
        ),
    getTravelPolicies: (uuid: string, id, uuidParent?: string) =>
        fetchAction(
            CoreAPIService.get(
                `/rideManager/travel/restrictions/company/${uuid}${uuidParent ? `?id_parent=${uuidParent}` : ''}`,
            ),
            types.GET_TRAVEL_POLICIES,
            {
                id,
            },
        ),
    createTravelPolicy: (uuid: string, id, payload) =>
        fetchAction(
            CoreAPIService.post(`/rideManager/travel/restrictions`, payload),
            types.CREATE_TRAVEL_POLICY,
            {
                id,
            },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.companies}/${id}`));
                },
            },
        ),
    deleteTravelPolicies: (uuid: string, id) =>
        fetchAction(
            CoreAPIService.delete(`/rideManager/travel/restrictions/company/${uuid}`),
            types.DELETE_TRAVEL_POLICIES,
            {
                id,
            },
        ),
    forceBillRides: (groupId, callback?) =>
        fetchAction(
            CoreAPIService.post(`/rideManager/rides/force-billing`, { group_uuid: groupId }), // l'uuid du group dont on va biller toutes les rides created
            types.FORCE_BILLING,
            { groupId },
            { success: callback },
        ),
    // NEW Company
    getKamCompanyCount: (idKam: string, callback?) =>
        fetchAction(
            CoreAPIService.get(`/internal/contract/account_manager/${idKam}`, null, null, envConfig.id_url),
            types.GET_KAM_CONTACT_COUNT,
            { idKam },
            {
                success: callback,
            },
        ),
    updateCompaniesKamToKam: (payload: UpdateCompaniesKamToKamPayload, callback?) =>
        fetchAction(
            CoreAPIService.put(`/internal/contract/account_manager`, payload, null, envConfig.id_url),
            types.UPDATE_CONTRACT_KAM_TO_KAM,
            {},
            {
                success: callback,
            },
        ),
    getCompanyNew: (id, callback?) =>
        fetchAction(
            CoreAPIService.get(
                `internal/contract/${id}?getCompanyGroup=true&getSiren=true`,
                null,
                null,
                envConfig.privapi_url,
            ),
            types.GET_CONTRACT,
            { id },
            {
                success: callback,
            },
        ),
    editCompanyNew: (id, payload) =>
        fetchAction(
            CoreAPIService.put(`internal/contract/${id}`, payload, null, envConfig.privapi_url),
            types.EDIT_CONTRACT,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.contracts}/${id}`));
                },
            },
        ),
    getGroupNew: (id, callback?) =>
        fetchAction(
            CoreAPIService.get(`/internal/group/${id}`, null, null, envConfig.id_url),
            types.GET_CONTRACT_GROUP,
            { id },
            {
                success: callback,
            },
        ),
    getContractGroups: (id, callback?, errorCallback?) =>
        fetchAction(
            CoreAPIService.get(`/internal/contract/${id}/groups/list?sort=id&order=desc`, null, null, envConfig.id_url),
            types.GET_CONTRACT_GROUPS,
            { id },
            {
                success: callback,
                // LT-876: one contract's groups failing must not blank the
                // sheet, so the caller is told which one failed
                error: errorCallback,
            },
        ),
    getContractPaymentSettings: (id, callback?) =>
        fetchAction(
            CoreAPIService.get(`internal/contract/${id}/payment-settings`, null, null, envConfig.privapi_url),
            types.GET_CONTRACT_PAYMENT_SETTINGS,
            { id },
            {
                success: callback,
            },
        ),
    setGroupPaymentSettings: (
        idContract: string,
        payload: Pick<PaymentSettings, 'group_payment_policies'>,
        callback?,
    ) =>
        fetchAction(
            CoreAPIService.put(
                `internal/contract/${idContract}/payment-settings`,
                payload,
                null,
                envConfig.privapi_url,
            ),
            types.SET_GROUP_PAYMENT_SETTINGS,
            { id: idContract },
            {
                success: callback,
            },
        ),
    createPaymentMethodSepa: (payload: Sepa, callback?) =>
        fetchAction(
            CoreAPIService.post(`internal/payment-methods/sepa`, payload, null, envConfig.privapi_url),
            types.CREATE_PAYMENT_METHOD_SEPA,
            {},
            {
                success: callback,
            },
        ),
    createPaymentMethodAirplus: (payload: Airplus, callback?) =>
        fetchAction(
            CoreAPIService.post(`internal/payment-methods/airplus`, payload, null, envConfig.privapi_url),
            types.CREATE_PAYMENT_METHOD_AIRPLUS,
            {},
            {
                success: callback,
            },
        ),
};

const localState = (state: any): CompaniesState => state[name] || initialState;
export const selectors = {
    getGroupsNew: (state) => localState(state).groupsNew,
    getCompaniesNew: (state) => localState(state).companiesNew,
    getCompanies: (state) => localState(state).companies,
    getCompaniesList: (state) => localState(state).companies_list,
    getContacts: (state) => localState(state).contracts,
    getIndustries: (state) => localState(state).industries,
    getIndustriesNew: (state) => localState(state).industriesNew,
};
